import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Link2, Twitter, Facebook, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import Nav from "@/components/cobain/Nav";
import Footer from "@/components/cobain/Footer";
import NewsletterBand from "@/components/cobain/NewsletterBand";
import { Reveal } from "@/components/cobain/Reveal";
import { api, mediaUrl } from "@/lib/api";

const fmtDay = (iso) =>
  iso ? new Date(iso).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }).toUpperCase() : "";

export default function ArticlePage() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [related, setRelated] = useState([]);
  const [state, setState] = useState("loading");

  useEffect(() => {
    setState("loading");
    setArticle(null);
    api
      .getArticle(slug)
      .then((a) => {
        setArticle(a);
        setState("ok");
        api.getArticles().then((all) => setRelated(all.filter((x) => x.slug !== slug).slice(0, 3))).catch(() => {});
      })
      .catch(() => setState("missing"));
  }, [slug]);

  const copyLink = () => {
    navigator.clipboard?.writeText(window.location.href);
    toast.success("Link copied — pass it on.", { id: "copy-link" });
  };

  return (
    <main data-testid="article-page">
      <Nav />
      {state === "loading" && (
        <div className="min-h-screen flex items-center justify-center">
          <p className="font-mono text-xs tracking-[0.3em] text-clay animate-pulse" data-testid="article-loading">LOADING THE STORY...</p>
        </div>
      )}

      {state === "missing" && (
        <div className="min-h-screen flex flex-col items-center justify-center gap-6 px-5 text-center" data-testid="article-not-found">
          <h1 className="font-display text-6xl text-cream">STORY NOT FOUND.</h1>
          <Link to="/" className="font-mono text-xs tracking-[0.2em] text-burnt hover:text-burnt-hover" data-testid="article-back-home">
            ← BACK TO THE REPORT
          </Link>
        </div>
      )}

      {state === "ok" && article && (
        <article>
          <header className="relative pt-32 pb-10 md:pt-40 md:pb-16 max-w-[1000px] mx-auto px-5 md:px-10">
            <Reveal y={20}>
              <Link to="/" className="inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.2em] text-clay hover:text-tan transition-colors" data-testid="article-back">
                <ArrowLeft size={14} /> THE EDITORIAL
              </Link>
              <div className="mt-8 flex items-center gap-3 flex-wrap">
                <span className="font-mono text-[10px] tracking-[0.2em] bg-burnt text-cream px-3 py-1.5" data-testid="article-category">{article.category}</span>
                <span className="font-mono text-[10px] tracking-[0.2em] text-clay">{article.read_time}</span>
                <span className="font-mono text-[10px] tracking-[0.2em] text-clay" data-testid="article-date">{fmtDay(article.published_at)}</span>
              </div>
              <h1 className="mt-6 font-editorial font-extrabold text-cream text-3xl sm:text-5xl md:text-6xl leading-[1.05] tracking-tight" data-testid="article-title">
                {article.title}
              </h1>
              <p className="mt-6 font-mono text-[11px] tracking-[0.2em] text-tan">WORDS — {(article.author || "THE COBAIN REPORT").toUpperCase()}</p>
            </Reveal>
          </header>

          {article.image && (
            <Reveal y={30}>
              <div className="max-w-[1200px] mx-auto px-5 md:px-10">
                <div className="border border-woodborder overflow-hidden" data-testid="article-hero-image">
                  <img src={mediaUrl(article.image)} alt={article.title} className="w-full aspect-[16/8] object-cover" />
                </div>
              </div>
            </Reveal>
          )}

          <div className="max-w-[760px] mx-auto px-5 md:px-10 py-14 md:py-20" data-testid="article-body">
            {(typeof article.body === "string" ? article.body.split(/\n\n+/) : article.body || []).map((p, i) => (
              <Reveal key={i} y={24} delay={0.03 * i}>
                <p className={`text-sand font-light leading-[1.9] text-base md:text-lg mb-8 ${i === 0 ? "first-letter:font-display first-letter:text-7xl first-letter:text-burnt first-letter:float-left first-letter:mr-4 first-letter:leading-[0.8] first-letter:pt-2" : ""}`}>
                  {p}
                </p>
              </Reveal>
            ))}

            <div className="mt-12 pt-8 border-t border-woodborder flex flex-wrap items-center gap-3" data-testid="article-share">
              <span className="font-mono text-[10px] tracking-[0.25em] text-clay mr-2">SHARE THE STORY</span>
              <button onClick={copyLink} data-testid="article-share-copy" aria-label="Copy link"
                className="w-10 h-10 rounded-full border border-woodborder flex items-center justify-center text-sand hover:text-burnt hover:border-burnt transition-colors">
                <Link2 size={15} />
              </button>
              <a href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(article.title)}&url=${encodeURIComponent(window.location.href)}`}
                target="_blank" rel="noopener noreferrer" aria-label="Share on X" data-testid="article-share-x"
                className="w-10 h-10 rounded-full border border-woodborder flex items-center justify-center text-sand hover:text-burnt hover:border-burnt transition-colors">
                <Twitter size={15} />
              </a>
              <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`}
                target="_blank" rel="noopener noreferrer" aria-label="Share on Facebook" data-testid="article-share-facebook"
                className="w-10 h-10 rounded-full border border-woodborder flex items-center justify-center text-sand hover:text-burnt hover:border-burnt transition-colors">
                <Facebook size={15} />
              </a>
            </div>
          </div>

          {related.length > 0 && (
            <section className="max-w-[1200px] mx-auto px-5 md:px-10 pb-24" data-testid="article-related">
              <h2 className="font-display text-4xl md:text-5xl text-cream tracking-wide mb-10">KEEP READING.</h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {related.map((a, i) => (
                  <Link key={a.slug} to={`/read/${a.slug}`} className="group block" data-testid={`article-related-${i}`}>
                    <div className="overflow-hidden border border-woodborder">
                      <img src={mediaUrl(a.image)} alt={a.title} loading="lazy" className="w-full aspect-[4/3] object-cover grayscale-[0.4] group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700" />
                    </div>
                    <div className="pt-4 flex items-start justify-between gap-3">
                      <div>
                        <span className="font-mono text-[9px] tracking-[0.2em] text-burnt">{a.category}</span>
                        <h3 className="mt-1 font-editorial font-bold text-cream leading-snug group-hover:text-tan-light transition-colors">{a.title}</h3>
                      </div>
                      <ArrowUpRight size={16} className="text-clay group-hover:text-burnt transition-colors shrink-0 mt-1" />
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}

          <NewsletterBand />
        </article>
      )}
      <Footer />
    </main>
  );
}
